import type { PaymentBucket } from '../../types/dashboard'
import { fmt } from '../../lib/dashboardHelpers'
import { Doughnut } from 'react-chartjs-2'
import { useThemeStore } from '../../stores/themeStore'
import { useChartColors } from '../../hooks/useChartColors'

export default function PaymentDonut({ mix }: { mix: PaymentBucket[] }) {
    const colorMode = useThemeStore(s => s.getEffectiveColorMode())
    const colors = useChartColors()
    const total = mix.reduce((s, m) => s + m.amount, 0)
    const threshold = total * 0.03
    const main = mix.filter(m => m.amount >= threshold)
    const otherAmt = mix.filter(m => m.amount < threshold).reduce((s, m) => s + m.amount, 0)
    if (otherAmt > 0) main.push({ name: 'Other', shortName: 'Other', amount: otherAmt, count: 0 })

    const legendColor = colorMode === 'dark' ? '#e2e8f0' : '#475569'

    const data = {
        labels: main.map(m => m.name),
        datasets: [{
            data: main.map(m => m.amount),
            backgroundColor: colors.donut.slice(0, main.length),
            borderWidth: 0, hoverOffset: 6
        }]
    }

    return (
        <div className="chart-panel" style={{ height: '100%' }}>
            <div className="chart-panel-title">Payment Mix</div>
            <div className="chart-panel-content">
                <Doughnut data={data} options={{
                    responsive: true, maintainAspectRatio: false, cutout: '62%',
                    plugins: {
                        legend: {
                            position: 'bottom',
                            labels: {
                                usePointStyle: true, pointStyle: 'circle', boxWidth: 8,
                                color: legendColor, font: { size: 11 }, padding: 12,
                                generateLabels: (chart) => {
                                    const ds = chart.data.datasets[0]
                                    return chart.data.labels!.map((lbl, i) => {
                                        const val = (ds.data[i] as number)
                                        const pct = total > 0 ? ((val / total) * 100).toFixed(0) : '0'
                                        return {
                                            text: `${lbl} — ${fmt(val, 'currency')} (${pct}%)`,
                                            fillStyle: (ds.backgroundColor as string[])[i],
                                            fontColor: legendColor, index: i
                                        }
                                    })
                                }
                            }
                        },
                        tooltip: {
                            backgroundColor: 'rgba(15, 23, 42, 0.95)',
                            callbacks: {
                                label: (ctx) => {
                                    const val = ctx.parsed
                                    const pct = total > 0 ? ((val / total) * 100).toFixed(1) : '0'
                                    return ` ${ctx.label}: $${val.toLocaleString()} (${pct}%)`
                                }
                            }
                        }
                    }
                }} />
            </div>
        </div>
    )
}
